/**
 * ActorAlignmentPanel Component
 * 
 * Displays the Customer Actor panel with:
 * - Actor identity (name, role)
 * - Actor priorities (what they care about)
 * - Actor motivations (what drives them)
 * - Alignment score slider for subjective scoring
 */

import React, { useState } from 'react';
import { 
  Slider, 
  Tooltip, 
  Empty,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  message 
} from 'antd';
import {
  UserOutlined,
  StarOutlined,
  HeartOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { 
  CustomerActor, 
  ActorPriority, 
  ActorMotivation,
} from '../../types/marketFit';
import { useMarketFit } from '../../contexts/MarketFitContext';
import styles from './ActorAlignmentPanel.module.css';

interface ActorAlignmentPanelProps {
  actor: CustomerActor | null;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface PriorityItemProps {
  priority: ActorPriority;
  onEdit: () => void;
  onDelete: () => void;
}

const PriorityItem: React.FC<PriorityItemProps> = ({
  priority,
  onEdit,
  onDelete,
}) => (
  <div className={styles.listItem}>
    <div className={styles.listItemContent}>
      <div className={styles.rankBadge}>#{priority.rank}</div>
      <div className={styles.listItemInfo}>
        <span className={styles.listItemName}>{priority.name}</span>
        {priority.description && (
          <span className={styles.listItemDescription}>{priority.description}</span>
        )}
      </div>
    </div>
    <div className={styles.listItemActions}>
      <Tooltip title="Edit">
        <button 
          className={styles.actionButton}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
          <EditOutlined />
        </button>
      </Tooltip>
      <Tooltip title="Delete">
        <button 
          className={`${styles.actionButton} ${styles.actionButtonDelete}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          <DeleteOutlined />
        </button>
      </Tooltip>
    </div>
  </div>
);

interface MotivationItemProps {
  motivation: ActorMotivation;
  onEdit: () => void;
  onDelete: () => void;
}

const MotivationItem: React.FC<MotivationItemProps> = ({
  motivation,
  onEdit,
  onDelete,
}) => {
  const getStrengthClass = () => {
    switch (motivation.strength) {
      case 'strong': return styles.strengthStrong;
      case 'moderate': return styles.strengthModerate;
      case 'weak': return styles.strengthWeak;
      default: return '';
    }
  };

  return (
    <div className={styles.listItem}>
      <div className={styles.listItemContent}>
        <HeartOutlined className={`${styles.motivationIcon} ${getStrengthClass()}`} />
        <div className={styles.listItemInfo}>
          <span className={styles.listItemName}>{motivation.name}</span>
          {motivation.description && (
            <span className={styles.listItemDescription}>{motivation.description}</span>
          )}
        </div>
      </div>
      <div className={styles.listItemMeta}>
        <span className={`${styles.strengthBadge} ${getStrengthClass()}`}>
          {motivation.strength}
        </span>
        <div className={styles.listItemActions}>
          <Tooltip title="Edit">
            <button 
              className={styles.actionButton}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button 
              className={`${styles.actionButton} ${styles.actionButtonDelete}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ActorAlignmentPanel: React.FC<ActorAlignmentPanelProps> = ({
  actor,
}) => {
  const { 
    scores, 
    setActorSituationAlignment,
  } = useMarketFit();

  const [priorityModalVisible, setPriorityModalVisible] = useState(false);
  const [motivationModalVisible, setMotivationModalVisible] = useState(false);
  const [_editingPriority, setEditingPriority] = useState<ActorPriority | null>(null);
  const [_editingMotivation, setEditingMotivation] = useState<ActorMotivation | null>(null);
  
  const [priorityForm] = Form.useForm();
  const [motivationForm] = Form.useForm();

  const handleAlignmentChange = (value: number) => {
    setActorSituationAlignment(value / 100);
  };

  const getAlignmentColor = (value: number): string => {
    if (value >= 70) return '#22c55e';
    if (value >= 40) return '#fbbf24';
    return '#ef4444';
  };

  // Priority handlers
  const handleAddPriority = () => {
    setEditingPriority(null);
    priorityForm.resetFields();
    priorityForm.setFieldsValue({ rank: (actor?.priorities.length ?? 0) + 1 });
    setPriorityModalVisible(true);
  };

  const handleEditPriority = (priority: ActorPriority) => {
    setEditingPriority(priority);
    priorityForm.setFieldsValue(priority);
    setPriorityModalVisible(true);
  };

  const handleDeletePriority = (_priorityId: string) => {
    void message.info('Priority deleted (mock)');
  };

  const handlePrioritySubmit = () => {
    priorityForm.validateFields().then(_values => {
      void message.success('Priority saved (mock)');
      setPriorityModalVisible(false);
      priorityForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  // Motivation handlers
  const handleAddMotivation = () => {
    setEditingMotivation(null);
    motivationForm.resetFields();
    motivationForm.setFieldsValue({ strength: 'moderate' });
    setMotivationModalVisible(true);
  };

  const handleEditMotivation = (motivation: ActorMotivation) => {
    setEditingMotivation(motivation);
    motivationForm.setFieldsValue(motivation);
    setMotivationModalVisible(true);
  };

  const handleDeleteMotivation = (_motivationId: string) => {
    void message.info('Motivation deleted (mock)');
  };

  const handleMotivationSubmit = () => {
    motivationForm.validateFields().then(_values => {
      void message.success('Motivation saved (mock)');
      setMotivationModalVisible(false);
      motivationForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  const handleAISuggest = () => {
    void message.info('AI suggestion feature coming soon');
  };

  const alignmentValue = Math.round(scores.actorSituationAlignment * 100);
  const priorities = actor?.priorities ?? [];
  const motivations = actor?.motivations ?? [];

  if (!actor) {
    return (
      <div className={styles.actorPanel}>
        <div className={styles.emptyState}>
          <UserOutlined className={styles.emptyIcon} />
          <p className={styles.emptyText}>No actor selected</p>
          <Button type="primary" size="small" icon={<PlusOutlined />}>
            Add Actor
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.actorPanel}>
      {/* Actor Header */}
      <div className={styles.actorHeader}>
        <div className={styles.actorInfo}>
          <div className={styles.actorAvatar}>
            <UserOutlined />
          </div>
          <div className={styles.actorDetails}>
            <span className={styles.actorName}>{actor.actorName}</span>
            <span className={styles.actorRole}>{actor.actorRole}</span>
          </div>
        </div>
        
        <div className={styles.alignmentSection}>
          <div className={styles.alignmentLabel}>Actor-Situation Alignment</div>
          <div 
            className={styles.alignmentValue}
            style={{ color: getAlignmentColor(alignmentValue) }}
          >
            {alignmentValue}%
          </div>
        </div>
      </div>

      {/* Alignment Slider */}
      <div className={styles.sliderSection}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>Set Alignment Score</span>
          <Tooltip title="Use AI to suggest alignment based on priorities vs needs">
            <Button 
              type="text" 
              size="small" 
              icon={<RobotOutlined />}
              onClick={handleAISuggest}
              className={styles.aiButton}
            >
              AI Suggest
            </Button>
          </Tooltip>
        </div>
        <Slider
          value={alignmentValue}
          onChange={handleAlignmentChange}
          min={0}
          max={100}
          tooltip={{
            formatter: (value) => `${value}%`,
          }}
          marks={{
            0: { label: '0%', style: { color: '#ef4444' } },
            50: { label: '50%', style: { color: '#fbbf24' } },
            100: { label: '100%', style: { color: '#22c55e' } },
          }}
          className={styles.alignmentSlider}
        />
        <div className={styles.sliderHint}>
          How well does this actor's priorities align with addressing the situation needs?
        </div>
      </div>

      {/* Two-column layout for Priorities and Motivations */}
      <div className={styles.actorSections}>
        {/* Priorities Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <StarOutlined className={styles.sectionIcon} />
              <span>Priorities</span>
              <span className={styles.sectionCount}>({priorities.length})</span>
            </div>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={handleAddPriority}
              className={styles.addButton}
            >
              Add
            </Button>
          </div>
          
          <div className={styles.listContainer}>
            {priorities.length === 0 ? (
              <Empty 
                description="No priorities defined" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className={styles.emptyList}
              />
            ) : (
              priorities
                .sort((a, b) => a.rank - b.rank)
                .map(priority => (
                  <PriorityItem
                    key={priority.id}
                    priority={priority}
                    onEdit={() => handleEditPriority(priority)}
                    onDelete={() => handleDeletePriority(priority.id)}
                  />
                ))
            )}
          </div>
        </div>

        {/* Motivations Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <HeartOutlined className={styles.sectionIcon} />
              <span>Motivations</span>
              <span className={styles.sectionCount}>({motivations.length})</span>
            </div>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={handleAddMotivation}
              className={styles.addButton}
            >
              Add
            </Button>
          </div>
          
          <div className={styles.listContainer}>
            {motivations.length === 0 ? (
              <Empty 
                description="No motivations defined" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className={styles.emptyList}
              />
            ) : (
              motivations.map(motivation => (
                <MotivationItem
                  key={motivation.id}
                  motivation={motivation}
                  onEdit={() => handleEditMotivation(motivation)}
                  onDelete={() => handleDeleteMotivation(motivation.id)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Priority Modal */}
      <Modal
        title="Add Priority"
        open={priorityModalVisible}
        onOk={handlePrioritySubmit}
        onCancel={() => setPriorityModalVisible(false)}
        okText="Save"
        className={styles.formModal}
      >
        <Form form={priorityForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Priority Name" 
            rules={[{ required: true, message: 'Please enter priority name' }]}
          >
            <Input placeholder="e.g., Cost Reduction, Time to Value, Risk Mitigation" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              placeholder="Describe why this is a priority for this actor" 
              rows={3}
            />
          </Form.Item>
          <Form.Item 
            name="rank" 
            label="Priority Rank"
            rules={[{ required: true, message: 'Please enter rank' }]}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Motivation Modal */}
      <Modal
        title="Add Motivation"
        open={motivationModalVisible}
        onOk={handleMotivationSubmit}
        onCancel={() => setMotivationModalVisible(false)}
        okText="Save"
        className={styles.formModal}
      >
        <Form form={motivationForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Motivation Name" 
            rules={[{ required: true, message: 'Please enter motivation name' }]}
          >
            <Input placeholder="e.g., Career Advancement, Team Recognition, Process Improvement" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              placeholder="Describe this motivation and how it drives the actor" 
              rows={3}
            />
          </Form.Item>
          <Form.Item 
            name="strength" 
            label="Strength"
            rules={[{ required: true, message: 'Please select strength' }]}
          >
            <Select placeholder="Select strength">
              <Select.Option value="strong">Strong</Select.Option>
              <Select.Option value="moderate">Moderate</Select.Option>
              <Select.Option value="weak">Weak</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ActorAlignmentPanel;
